import { DateTime } from "luxon";
import Slider from "rc-slider";
import "rc-slider/assets/index.css";
import { forwardRef, useEffect, useImperativeHandle, useState } from "react";

const DAYS = [
  { key: 1, label: "Monday" },
  { key: 2, label: "Tuesday" },
  { key: 3, label: "Wednesday" },
  { key: 4, label: "Thursday" },
  { key: 5, label: "Friday" },
  { key: 6, label: "Saturday" },
  { key: 7, label: "Sunday" },
];

const DeliveryHoursSettings = forwardRef(({ customer, onSave }, ref) => {
  const [weekdaysRange, setWeekdaysRange] = useState([8, 22]);
  const [dayRanges, setDayRanges] = useState({
    1: [8, 22],
    2: [8, 22],
    3: [8, 22],
    4: [8, 22],
    5: [8, 22],
    6: [8, 22],
    7: [8, 22],
  });
  const [timezone, setTimezone] = useState("Europe/Paris");
  const [currentTime, setCurrentTime] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [filteredTimezones, setFilteredTimezones] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [viewMode, setViewMode] = useState("grouped");
  const [saturdayRange, setSaturdayRange] = useState([8, 22]);
  const [sundayRange, setSundayRange] = useState([8, 22]);

  useEffect(() => {
    if (customer?.deliveryHours) {
      const dh = customer.deliveryHours;
      setTimezone(dh.timezone || "Europe/Paris");

      if (dh[1] !== undefined) {
        const newDayRanges = {};
        for (let i = 1; i <= 7; i++) {
          if (dh[i]) {
            newDayRanges[i] = [dh[i].from.hour, dh[i].to.hour];
          } else {
            newDayRanges[i] = [8, 22];
          }
        }
        setDayRanges(newDayRanges);
        setWeekdaysRange([newDayRanges[1][0], newDayRanges[1][1]]);
        setSaturdayRange([newDayRanges[6][0], newDayRanges[6][1]]);
        setSundayRange([newDayRanges[7][0], newDayRanges[7][1]]);

        const areWeekdaysHomogeneous =
          newDayRanges[1][0] === newDayRanges[2][0] &&
          newDayRanges[1][0] === newDayRanges[3][0] &&
          newDayRanges[1][0] === newDayRanges[4][0] &&
          newDayRanges[1][0] === newDayRanges[5][0] &&
          newDayRanges[1][1] === newDayRanges[2][1] &&
          newDayRanges[1][1] === newDayRanges[3][1] &&
          newDayRanges[1][1] === newDayRanges[4][1] &&
          newDayRanges[1][1] === newDayRanges[5][1];

        if (!areWeekdaysHomogeneous) {
          setViewMode("individual");
        }
      } else {
        const range = [dh.from.hour, dh.to.hour];
        setWeekdaysRange(range);
        setSaturdayRange(range);
        setSundayRange(range);
        const newDayRanges = {};
        for (let i = 1; i <= 7; i++) {
          newDayRanges[i] = [...range];
        }
        setDayRanges(newDayRanges);
      }
    }
  }, [customer]);

  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === "Escape") {
        setShowDropdown(false);
        setSearchTerm("");
      }
    };
    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, []);

  useEffect(() => {
    const updateTime = () => {
      const now = DateTime.now().setZone(timezone);
      setCurrentTime(now.toFormat("HH:mm"));
    };
    updateTime();
    const interval = setInterval(updateTime, 60000);
    return () => clearInterval(interval);
  }, [timezone]);

  useEffect(() => {
    const allTimezones = Intl.supportedValuesOf("timeZone");
    if (searchTerm === "") {
      setFilteredTimezones(allTimezones.slice(0, 10));
    } else {
      const filtered = allTimezones.filter((tz) =>
        tz.toLowerCase().includes(searchTerm.toLowerCase()),
      );
      setFilteredTimezones(filtered.slice(0, 10));
    }
  }, [searchTerm]);

  const getTimezoneLabel = (tz) => {
    const now = DateTime.now().setZone(tz);
    const offset = now.toFormat("ZZ");
    return `${tz} (UTC${offset})`;
  };

  const handleDayChange = (dayKey, newRange) => {
    setDayRanges({
      ...dayRanges,
      [dayKey]: newRange,
    });
  };

  const isNightRisk = (range) => {
    return range[0] < 8 || range[1] > 22;
  };

  const hasAnyNightRisk = () => {
    return Object.values(dayRanges).some((range) => isNightRisk(range));
  };

  const getSliderMarks = () => {
    const marks = {};
    for (let i = 0; i <= 24; i += 1) {
      marks[i] = `${i}h`;
    }
    return marks;
  };

  const formatTimeRange = (start, end) => {
    return {
      start: `${String(start).padStart(2, "0")}:00`,
      end: `${String(end).padStart(2, "0")}:00`,
    };
  };

  const getParisTimeEquivalent = (hour, minute = 0) => {
    if (timezone === "Europe/Paris") {
      return null;
    }
    const now = DateTime.now();
    const selectedTzTime = now.setZone(timezone).set({ hour, minute });
    const parisTime = selectedTzTime.setZone("Europe/Paris");
    return parisTime.toFormat("HH:mm");
  };

  const handleSave = async () => {
    const deliveryHours = {};
    for (let i = 1; i <= 7; i++) {
      deliveryHours[i] = {
        from: { hour: dayRanges[i][0], minute: 0 },
        to: { hour: dayRanges[i][1], minute: 0 },
      };
    }
    deliveryHours.timezone = timezone;

    await onSave({
      deliveryHours,
    });
  };

  useImperativeHandle(ref, () => ({
    save: handleSave,
  }));

  return (
    <div className="p-4 border rounded-lg bg-white shadow-sm">
      <div className="mb-4 text-sm text-gray-600">
        Current time in {timezone}: <strong>{currentTime}</strong>
      </div>

      <div className="mb-6">
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Timezone
        </label>
        <input
          type="text"
          placeholder="Search timezone..."
          value={showDropdown ? searchTerm : getTimezoneLabel(timezone)}
          onChange={(e) => {
            setSearchTerm(e.target.value);
            setShowDropdown(true);
          }}
          onFocus={() => setShowDropdown(true)}
          className="w-full p-2 border rounded mb-2"
        />
        {showDropdown && (
          <div className="max-h-48 overflow-y-auto border rounded">
            {filteredTimezones.map((tz) => (
              <div
                key={tz}
                onClick={() => {
                  setTimezone(tz);
                  setSearchTerm("");
                  setShowDropdown(false);
                }}
                className={`p-2 cursor-pointer hover:bg-gray-100 ${
                  tz === timezone ? "bg-blue-50 font-semibold" : ""
                }`}
              >
                {getTimezoneLabel(tz)}
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="mb-6">
        <div className="flex items-center justify-between mb-4">
          <div className="text-sm font-medium text-gray-700">
            Delivery Hours Configuration
          </div>
          <button
            type="button"
            onClick={() =>
              setViewMode(viewMode === "grouped" ? "individual" : "grouped")
            }
            className="px-3 py-1 text-xs rounded bg-gray-200 text-gray-700 hover:bg-gray-300"
          >
            {viewMode === "grouped"
              ? "Show all days individually"
              : "Group by weekdays/weekend"}
          </button>
        </div>

        {viewMode === "grouped" ? (
          <div className="space-y-6">
            <div className="p-4 bg-blue-50 border border-blue-200 rounded">
              <div className="mb-3 text-sm font-medium text-gray-700">
                Weekdays (Monday to Friday)
              </div>
              <Slider
                range
                min={0}
                max={24}
                step={1}
                value={weekdaysRange}
                onChange={(newRange) => {
                  setWeekdaysRange(newRange);
                  const newDayRanges = { ...dayRanges };
                  for (let i = 1; i <= 5; i++) {
                    newDayRanges[i] = [...newRange];
                  }
                  setDayRanges(newDayRanges);
                }}
                marks={getSliderMarks()}
                styles={{
                  track: {
                    backgroundColor: "#3b82f6",
                    height: "10px",
                    top: "2px",
                  },
                  handle: { borderColor: "#3b82f6" },
                  rail: { height: "10px", top: "2px" },
                }}
              />
              <div className="mt-6 text-sm text-gray-700">
                <div className="font-medium mb-1">Sending window:</div>
                <div>
                  Start:{" "}
                  <strong>
                    {formatTimeRange(weekdaysRange[0], weekdaysRange[1]).start}
                  </strong>
                  {getParisTimeEquivalent(weekdaysRange[0]) && (
                    <span className="text-gray-500 ml-2">
                      ({getParisTimeEquivalent(weekdaysRange[0])} Paris time)
                    </span>
                  )}{" "}
                  | End:{" "}
                  <strong>
                    {formatTimeRange(weekdaysRange[0], weekdaysRange[1]).end}
                  </strong>
                  {getParisTimeEquivalent(weekdaysRange[1]) && (
                    <span className="text-gray-500 ml-2">
                      ({getParisTimeEquivalent(weekdaysRange[1])} Paris time)
                    </span>
                  )}
                </div>
              </div>
              {isNightRisk(weekdaysRange) && (
                <div className="mt-3 p-2 bg-amber-50 border-l-4 border-amber-500 text-amber-700 text-xs">
                  ⚠️ Sending messages between 22h-8h may impact WhatsApp account
                  quality.
                </div>
              )}
            </div>

            <div className="p-4 bg-green-50 border border-green-200 rounded">
              <div className="mb-3 text-sm font-medium text-gray-700">
                Saturday
              </div>
              <Slider
                range
                min={0}
                max={24}
                step={1}
                value={saturdayRange}
                onChange={(newRange) => {
                  setSaturdayRange(newRange);
                  setDayRanges({
                    ...dayRanges,
                    6: [...newRange],
                  });
                }}
                marks={getSliderMarks()}
                styles={{
                  track: {
                    backgroundColor: "#22c55e",
                    height: "10px",
                    top: "2px",
                  },
                  handle: { borderColor: "#3b82f6" },
                  rail: { height: "10px", top: "2px" },
                }}
              />
              <div className="mt-6 text-sm text-gray-700">
                <div className="font-medium mb-1">Sending window:</div>
                <div>
                  Start:{" "}
                  <strong>
                    {formatTimeRange(saturdayRange[0], saturdayRange[1]).start}
                  </strong>
                  {getParisTimeEquivalent(saturdayRange[0]) && (
                    <span className="text-gray-500 ml-2">
                      ({getParisTimeEquivalent(saturdayRange[0])} Paris time)
                    </span>
                  )}{" "}
                  | End:{" "}
                  <strong>
                    {formatTimeRange(saturdayRange[0], saturdayRange[1]).end}
                  </strong>
                  {getParisTimeEquivalent(saturdayRange[1]) && (
                    <span className="text-gray-500 ml-2">
                      ({getParisTimeEquivalent(saturdayRange[1])} Paris time)
                    </span>
                  )}
                </div>
              </div>
              {isNightRisk(saturdayRange) && (
                <div className="mt-3 p-2 bg-amber-50 border-l-4 border-amber-500 text-amber-700 text-xs">
                  ⚠️ Night hours detected
                </div>
              )}
            </div>

            <div className="p-4 bg-purple-50 border border-purple-200 rounded">
              <div className="mb-3 text-sm font-medium text-gray-700">
                Sunday
              </div>
              <Slider
                range
                min={0}
                max={24}
                step={1}
                value={sundayRange}
                onChange={(newRange) => {
                  setSundayRange(newRange);
                  setDayRanges({
                    ...dayRanges,
                    7: [...newRange],
                  });
                }}
                marks={getSliderMarks()}
                styles={{
                  track: {
                    backgroundColor: "#a855f7",
                    height: "10px",
                    top: "2px",
                  },
                  handle: { borderColor: "#3b82f6" },
                  rail: { height: "10px", top: "2px" },
                }}
              />
              <div className="mt-6 text-sm text-gray-700">
                <div className="font-medium mb-1">Sending window:</div>
                <div>
                  Start:{" "}
                  <strong>
                    {formatTimeRange(sundayRange[0], sundayRange[1]).start}
                  </strong>
                  {getParisTimeEquivalent(sundayRange[0]) && (
                    <span className="text-gray-500 ml-2">
                      ({getParisTimeEquivalent(sundayRange[0])} Paris time)
                    </span>
                  )}{" "}
                  | End:{" "}
                  <strong>
                    {formatTimeRange(sundayRange[0], sundayRange[1]).end}
                  </strong>
                  {getParisTimeEquivalent(sundayRange[1]) && (
                    <span className="text-gray-500 ml-2">
                      ({getParisTimeEquivalent(sundayRange[1])} Paris time)
                    </span>
                  )}
                </div>
              </div>
              {isNightRisk(sundayRange) && (
                <div className="mt-3 p-2 bg-amber-50 border-l-4 border-amber-500 text-amber-700 text-xs">
                  ⚠️ Night hours detected
                </div>
              )}
            </div>
          </div>
        ) : (
          <div className="space-y-6">
            {DAYS.map((day) => (
              <div key={day.key} className="px-4">
                <div className="mb-2 text-sm font-medium text-gray-700">
                  {day.label}
                </div>
                <Slider
                  range
                  min={0}
                  max={24}
                  step={1}
                  value={dayRanges[day.key]}
                  onChange={(newRange) => handleDayChange(day.key, newRange)}
                  marks={getSliderMarks()}
                  styles={{
                    track: {
                      backgroundColor: "#22c55e",
                      height: "10px",
                      top: "2px",
                    },
                    handle: { borderColor: "#3b82f6" },
                    rail: { height: "10px", top: "2px" },
                  }}
                />
                <div className="mt-4 text-sm text-gray-700">
                  <div className="mt-5">
                    Start:{" "}
                    <strong>
                      {
                        formatTimeRange(
                          dayRanges[day.key][0],
                          dayRanges[day.key][1],
                        ).start
                      }
                    </strong>
                    {getParisTimeEquivalent(dayRanges[day.key][0]) && (
                      <span className="text-gray-500 ml-2">
                        ({getParisTimeEquivalent(dayRanges[day.key][0])} Paris
                        time)
                      </span>
                    )}{" "}
                    | End:{" "}
                    <strong>
                      {
                        formatTimeRange(
                          dayRanges[day.key][0],
                          dayRanges[day.key][1],
                        ).end
                      }
                    </strong>
                    {getParisTimeEquivalent(dayRanges[day.key][1]) && (
                      <span className="text-gray-500 ml-2">
                        ({getParisTimeEquivalent(dayRanges[day.key][1])} Paris
                        time)
                      </span>
                    )}
                  </div>
                </div>
                {isNightRisk(dayRanges[day.key]) && (
                  <div className="mt-2 p-2 bg-amber-50 border-l-4 border-amber-500 text-amber-700 text-xs">
                    ⚠️ Night hours detected
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {hasAnyNightRisk() && (
        <div className="mt-6 p-3 bg-amber-50 border-l-4 border-amber-500 text-amber-700 text-sm">
          ⚠️ <strong>Warning:</strong> Sending messages between 22h-8h may
          impact WhatsApp account quality (high report rate).
        </div>
      )}
    </div>
  );
});

DeliveryHoursSettings.displayName = "DeliveryHoursSettings";

export default DeliveryHoursSettings;
